// P0 · Cadastro Inteligente — upload de fotos/PDF, IA extrai e a secretaria confirma campo a campo.
// A IA nunca grava nada: cada linha aceita explicitamente é o que vai para o cadastro oficial.
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sparkles, UploadCloud, FileText, Image as ImageIcon, X, AlertTriangle, CheckCircle2, UserPlus, ShieldAlert, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import DocumentViewer from './DocumentViewer';
import { FamilyParentsSummary } from './FamilyParentsSummary';
import { EnrollmentFilesOverview } from './EnrollmentFilesOverview';

const RELATIONS = ['Pai/Mãe', 'Pai', 'Mãe', 'Avô/Avó', 'Tio/Tia', 'Padrasto/Madrasta', 'Tutor legal', 'Outro'];

const confColor = (c) => (c >= 95 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
  : c >= 80 ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-rose-700 bg-rose-50 border-rose-200');

const STATUS_BADGE = {
  faltante: { label: 'Em branco no cadastro', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  divergente: { label: 'Divergente', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  igual: { label: 'Igual ao cadastro', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export function SmartImportButton({ className, label = 'Importar documentos com IA', ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" className={className}
        data-testid="btn-smart-import-open" onClick={() => setOpen(true)}>
        <Sparkles className="w-4 h-4 mr-1.5 text-violet-600" /> {label}
      </Button>
      <SmartImportModal open={open} onOpenChange={setOpen} {...props} />
    </>
  );
}

export default function SmartImportModal({ open, onOpenChange, studentId, processId, studentName, initialResult, onApplied }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [sel, setSel] = useState({});
  const [done, setDone] = useState(null);
  const [finals, setFinals] = useState({});      // valor FINAL por campo (editável na conferência)
  const [activeDoc, setActiveDoc] = useState(null);
  const inputRef = useRef(null);
  const appliedRef = useRef(false);   // só avisa o pai ao fechar: recarregar a página desmontaria o modal

  const reset = () => { setFiles([]); setResult(null); setSel({}); setDone(null); setFinals({}); setActiveDoc(null); };

  const close = () => {
    onOpenChange(false);
    reset();
    if (appliedRef.current) { const d = appliedRef.current; appliedRef.current = false; onApplied && onApplied(d); }
  };

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => /image\/|application\/pdf/.test(f.type));
    if (!arr.length) return toast.error('Envie apenas imagens (JPG/PNG) ou PDF.');
    setFiles((prev) => [...prev, ...arr].slice(0, 12));
  };

  const seed = (data) => {
    const init = {};
    (data.documents || []).forEach((d) => (d.persons || []).forEach((p) => {
      init[p.person_key] = {
        createNew: false,
        relation: p.role === 'responsavel' ? 'Pai/Mãe' : 'Outro',
        isFinancial: false,
        fields: (p.fields || []).filter((f) => f.suggested && !f.protected).map((f) => f.field),
      };
    }));
    setSel(init);
  };

  // Sessão já analisada (ex.: envios da família) abre direto na conferência
  useEffect(() => {
    if (open && initialResult) { setResult(initialResult); seed(initialResult); }
  }, [open, initialResult]);

  const analyze = async () => {
    if (!files.length) return toast.error('Selecione ao menos um documento.');
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (studentId) fd.append('student_id', studentId);
      if (processId) fd.append('process_id', processId);
      const { data } = await api.post('/smart-import/analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      setResult(data);
      seed(data);
      toast.success('Leitura concluída. Confira os dados antes de gravar.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao analisar os documentos.');
    } finally { setBusy(false); }
  };

  const toggleField = (pk, field) => setSel((s) => {
    const cur = s[pk] || { fields: [] };
    const has = cur.fields.includes(field);
    return { ...s, [pk]: { ...cur, fields: has ? cur.fields.filter((f) => f !== field) : [...cur.fields, field] } };
  });

  const persons = (result?.documents || []).flatMap((d) => (d.persons || []).map((p) => ({ ...p, _doc: d })));
  const totalChecked = persons.reduce((acc, p) => {
    const s = sel[p.person_key];
    if (!s) return acc;
    if (p.target_type === 'new_responsible' && !s.createNew) return acc;
    if (p.target_type === 'unmatched_student') return acc;
    return acc + s.fields.length + (s.linkToStudent ? 1 : 0);
  }, 0);

  const changeTarget = async (person, value) => {
    const [target_type, target_id] = value.split('::');
    try {
      const { data } = await api.post(`/smart-import/sessions/${result.session_id}/retarget`, {
        person_key: person.person_key, target_type, target_id: target_id || null,
      });
      setResult((r) => ({
        ...r,
        documents: (r.documents || []).map((d) => ({
          ...d,
          persons: (d.persons || []).map((p) => (p.person_key === person.person_key ? data : p)),
        })),
      }));
      setSel((s) => ({
        ...s,
        [person.person_key]: {
          ...(s[person.person_key] || {}), createNew: false, linkToStudent: false,
          fields: (data.fields || []).filter((f) => f.suggested).map((f) => f.field),
        },
      }));
      toast.success('Destino alterado. Confira os campos novamente.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível trocar o destino.');
    }
  };

  const confirm = async () => {
    const applications = persons.map((p) => {
      const s = sel[p.person_key] || { fields: [] };
      if (p.target_type === 'unmatched_student') return null;
      if (p.target_type === 'new_responsible' && !s.createNew) return null;
      if (!s.fields.length && !s.linkToStudent) return null;
      const byField = Object.fromEntries((p.fields || []).map((f) => [f.field, f.extracted_value]));
      return {
        person_key: p.person_key,
        target_type: p.target_type,
        target_id: p.target_id,
        relation: s.relation,
        is_financial: !!s.isFinancial,
        link_to_student: !!s.linkToStudent,
        create_new: !!s.createNew,
        apply_to_siblings: !!s.applyToSiblings,
        fields: s.fields.map((f) => {
          const finalValue = finals[`${p.person_key}:${f}`] ?? byField[f];
          return { field: f, value: finalValue, edited: finalValue !== byField[f] };
        }).filter((f) => (f.value || '').toString().trim()),
      };
    }).filter(Boolean);
    if (!applications.length) return toast.error('Marque ao menos um campo para gravar.');
    setSaving(true);
    try {
      const { data } = await api.post(`/smart-import/sessions/${result.session_id}/confirm`, { applications });
      setDone(data);
      window.dispatchEvent(new CustomEvent('student-guardians-updated', { detail: { studentId: result.student_id || studentId } }));
      toast.success(`${data.applied_count} informações aplicadas ao cadastro.`);
      appliedRef.current = data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gravar os dados.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) { onOpenChange(true); } else { close(); } }}>
      <DialogContent className="max-w-7xl w-[97vw] max-h-[94vh] overflow-y-auto" data-testid="smart-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" /> Cadastro Inteligente
          </DialogTitle>
          <DialogDescription>
            Envie fotos, prints ou PDFs (RG, CPF, certidão, comprovante de residência...). A IA lê e
            <b> sugere</b> os dados — nada é gravado sem o seu aceite. {studentName ? `Aluno: ${studentName}.` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* ── Resultado final ── */}
        {done ? (
          <div className="space-y-3" data-testid="smart-import-done">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="font-semibold text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> {done.applied_count} informações aplicadas ao cadastro.
              </p>
              {!!done.created_responsibles?.length && (
                <p className="text-sm text-emerald-700 mt-1">
                  Responsável(is) criado(s) e vinculado(s): {done.created_responsibles.map((r) => r.name).join(', ')}
                </p>
              )}
            </div>
            <div className="rounded-xl border p-4 flex items-center justify-between" data-testid="smart-import-completeness">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Completude do cadastro</p>
                <p className="text-sm text-slate-600">
                  Antes <b>{done.completeness_before?.percent ?? '-'}%</b> → agora
                  <b className="text-violet-700"> {done.completeness_after?.percent ?? '-'}%</b>
                </p>
              </div>
              <div className="w-40 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-violet-600 transition-all"
                  style={{ width: `${done.completeness_after?.percent || 0}%` }} />
              </div>
            </div>
            {!!done.completeness_after?.missing_labels?.length && (
              <p className="text-xs text-amber-700">
                Ainda faltando: {done.completeness_after.missing_labels.join(' · ')}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={reset} data-testid="smart-import-new-batch">Importar mais documentos</Button>
              <Button onClick={close} data-testid="smart-import-close">Fechar</Button>
            </DialogFooter>
          </div>
        ) : !result ? (
          /* ── Upload ── */
          <div className="space-y-3">
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="border-2 border-dashed border-violet-200 rounded-2xl p-10 text-center cursor-pointer hover:bg-violet-50/50 transition-colors"
              data-testid="smart-import-dropzone"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-violet-500 mb-2" />
              <p className="font-medium text-slate-700">Arraste os documentos aqui ou clique para escolher</p>
              <p className="text-xs text-slate-500 mt-1">JPG, PNG ou PDF · até 12 arquivos · 15MB cada</p>
              <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
                data-testid="smart-import-file-input"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {!!files.length && (
              <div className="space-y-1.5" data-testid="smart-import-file-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2">
                    {f.type === 'application/pdf' ? <FileText className="w-4 h-4 text-rose-500" /> : <ImageIcon className="w-4 h-4 text-blue-500" />}
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-slate-400">{Math.round(f.size / 1024)} KB</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      data-testid={`smart-import-remove-${i}`} className="text-slate-400 hover:text-rose-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancelar</Button>
              <Button onClick={analyze} disabled={busy || !files.length} data-testid="smart-import-analyze-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {busy ? 'Lendo documentos...' : 'Ler com IA'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* ── Conferência LADO A LADO: documento original × dados encontrados ── */
          <div className="space-y-3" data-testid="smart-import-review">
            <FamilyParentsSummary parents={result.family_parents} />
            <EnrollmentFilesOverview data={result.enrollment_files} activeId={activeDoc || (result.documents || [])[0]?.document_id}
              onOpen={(id) => (result.documents || []).some((d) => d.document_id === id) && setActiveDoc(id)} />
            <div className="flex flex-wrap items-center gap-1.5">
              {(result.documents || []).map((d) => (
                <button key={d.document_id} type="button" onClick={() => setActiveDoc(d.document_id)}
                  data-testid={`smart-import-doctab-${d.document_id}`}
                  className={`text-[11px] font-semibold rounded-full border px-2.5 py-1 transition-colors ${
                    (activeDoc || (result.documents || [])[0]?.document_id) === d.document_id
                      ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
                  {d.doc_type_label} · {(d.filename || '').slice(0, 22)}
                </button>
              ))}
            </div>

            {(() => {
              const d = (result.documents || []).find((x) => x.document_id === (activeDoc || (result.documents || [])[0]?.document_id))
                || (result.documents || [])[0];
              if (!d) return null;
              return (
                <div className="grid lg:grid-cols-2 gap-3 items-start">
                  <div className="lg:sticky lg:top-0">
                    <DocumentViewer documentId={d.document_id} filename={d.filename}
                      contentType={d.content_type} />
                    <p className="text-[10.5px] text-slate-500 mt-1">
                      Arquivo original preservado no cofre · {d.doc_type_label}
                      {d.doc_type_confidence != null ? ` · classificação ${d.doc_type_confidence}%` : ''}
                    </p>
                  </div>

                  <div className="space-y-3" data-testid="smart-import-fields-pane">
                    {d.error && (
                      <p className="text-sm text-rose-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> {d.error}
                      </p>
                    )}
                    {!d.error && !d.persons?.length && (
                      <p className="text-sm text-slate-500">Nenhum dado legível encontrado neste arquivo.</p>
                    )}

                    {(d.persons || []).map((p) => {
                      const s = sel[p.person_key] || { fields: [] };
                      const isNew = p.target_type === 'new_responsible';
                      const blocked = p.target_type === 'unmatched_student' || p.needs_review;
                      const disabled = blocked || (isNew && !s.createNew);
                      return (
                        <div key={p.person_key} className="rounded-xl border p-3 space-y-2"
                          data-testid={`smart-import-person-${p.person_key}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">
                              {p.role === 'aluno' ? 'Aluno' : p.role === 'responsavel' ? 'Responsável' : 'Pessoa'}
                            </Badge>
                            <span className="text-sm font-semibold">{p.extracted_name || '(sem nome)'}</span>
                            <span className="text-[11px] text-slate-500">→ {p.target_name || 'novo cadastro'} · {p.match_reason}</span>
                            {p.out_of_context && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Já existe em outra família</Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Aplicar em:</span>
                            <Select value={`${p.target_type}::${p.target_id || ''}`} onValueChange={(v) => changeTarget(p, v)}>
                              <SelectTrigger className="w-64 h-8 text-xs" data-testid={`smart-import-target-${p.person_key}`}>
                                <SelectValue placeholder="Escolher destino" />
                              </SelectTrigger>
                              <SelectContent>
                                {(result.targets || []).map((t) => (
                                  <SelectItem key={`${t.target_type}::${t.target_id || ''}`}
                                    value={`${t.target_type}::${t.target_id || ''}`}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {p.target_type === 'student' && !!result.siblings?.length
                            && (p.fields || []).some((f) => (result.address_fields || []).includes(f.field)) && (
                            <label className="flex items-center gap-2 text-xs cursor-pointer p-2 rounded-lg bg-teal-50/70 border border-teal-200">
                              <input type="checkbox" checked={!!s.applyToSiblings}
                                data-testid={`smart-import-siblings-${p.person_key}`}
                                onChange={(e) => setSel((st) => ({ ...st, [p.person_key]: { ...s, applyToSiblings: e.target.checked } }))} />
                              <Home className="w-3.5 h-3.5 text-teal-700" />
                              Aplicar o mesmo endereço aos irmãos ({result.siblings.length})
                            </label>
                          )}

                          {blocked && (
                            <p className="text-xs text-amber-700 flex items-center gap-1.5">
                              <ShieldAlert className="w-3.5 h-3.5" /> {p.needs_review ? 'Confira o responsável existente em “Aplicar em”. Nenhum cadastro será duplicado automaticamente.' : 'A IA não cria alunos. Cadastre o aluno e importe novamente.'}
                            </p>
                          )}

                          {p.out_of_context && (
                            <div className="flex flex-wrap items-center gap-3 p-2.5 rounded-lg bg-amber-50/70 border border-amber-200">
                              <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={!!s.linkToStudent}
                                  data-testid={`smart-import-link-${p.person_key}`}
                                  onChange={(e) => setSel((st) => ({ ...st, [p.person_key]: { ...s, linkToStudent: e.target.checked } }))} />
                                Vincular este responsável ao aluno (não duplica cadastro)
                              </label>
                              <Select value={s.relation}
                                onValueChange={(v) => setSel((st) => ({ ...st, [p.person_key]: { ...s, relation: v } }))}>
                                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`smart-import-link-relation-${p.person_key}`}>
                                  <SelectValue placeholder="Parentesco" />
                                </SelectTrigger>
                                <SelectContent>
                                  {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {isNew && (
                            <div className="flex flex-wrap items-center gap-3 p-2.5 rounded-lg bg-blue-50/60 border border-blue-100">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={!!s.createNew} disabled={!!p.needs_review}
                                  data-testid={`smart-import-create-new-${p.person_key}`}
                                  onChange={(e) => setSel((st) => ({ ...st, [p.person_key]: { ...s, createNew: e.target.checked } }))} />
                                <UserPlus className="w-4 h-4 text-blue-600" /> Criar novo responsável e vincular
                              </label>
                              <Select value={s.relation} disabled={!s.createNew}
                                onValueChange={(v) => setSel((st) => ({ ...st, [p.person_key]: { ...s, relation: v } }))}>
                                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`smart-import-relation-${p.person_key}`}>
                                  <SelectValue placeholder="Parentesco" />
                                </SelectTrigger>
                                <SelectContent>
                                  {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={!!s.isFinancial} disabled={!s.createNew}
                                  data-testid={`smart-import-financial-${p.person_key}`}
                                  onChange={(e) => setSel((st) => ({ ...st, [p.person_key]: { ...s, isFinancial: e.target.checked } }))} />
                                Responsável financeiro
                              </label>
                            </div>
                          )}

                          <div className="divide-y">
                            {(p.fields || []).map((f) => {
                              const key = `${p.person_key}:${f.field}`;
                              const finalValue = f.protected ? f.current_value : (finals[key] ?? f.extracted_value);
                              const checked = s.fields.includes(f.field);
                              return (
                                <div key={f.field} className="py-2 space-y-1"
                                  data-testid={`smart-import-row-${p.person_key}-${f.field}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[12.5px] font-semibold text-slate-700">{f.label}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${confColor(f.confidence)}`}>
                                      {f.confidence}%
                                    </span>
                                    <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[f.status].cls}`}>
                                      {STATUS_BADGE[f.status].label}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-[11.5px]">
                                    <p className="text-slate-500 truncate">
                                      Cadastro atual: <b className="text-slate-700">{f.current_value || '—'}</b>
                                    </p>
                                    <p className="text-slate-500 truncate">
                                      IA encontrou: <b className="text-slate-800">{f.extracted_value}</b>
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[11px] text-slate-500">Final</span>
                                    <Input value={finalValue} disabled={disabled || f.protected}
                                      className="h-8 text-[12.5px] flex-1 min-w-[140px]"
                                      data-testid={`smart-import-final-${p.person_key}-${f.field}`}
                                      onChange={(e) => {
                                        setFinals((v) => ({ ...v, [key]: e.target.value }));
                                        if (!checked) toggleField(p.person_key, f.field);
                                      }} />
                                    <Button type="button" size="sm" variant="outline" disabled={disabled || f.protected}
                                      className="h-8 text-[11px]"
                                      data-testid={`smart-import-keep-${p.person_key}-${f.field}`}
                                      onClick={() => {
                                        setFinals((v) => ({ ...v, [key]: f.current_value || '' }));
                                        if (checked) toggleField(p.person_key, f.field);
                                      }}>
                                      Manter atual
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" disabled={disabled || f.protected}
                                      className="h-8 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50"
                                      data-testid={`smart-import-use-${p.person_key}-${f.field}`}
                                      onClick={() => {
                                        setFinals((v) => ({ ...v, [key]: f.extracted_value }));
                                        if (!checked) toggleField(p.person_key, f.field);
                                      }}>
                                      Usar encontrado
                                    </Button>
                                    <label className="flex items-center gap-1.5 text-[11px] cursor-pointer ml-auto">
                                      <input type="checkbox" disabled={disabled || f.protected} checked={checked}
                                        data-testid={`smart-import-accept-${p.person_key}-${f.field}`}
                                        onChange={() => toggleField(p.person_key, f.field)} />
                                      Aplicar
                                    </label>
                                  </div>
                                  {f.protected && <p className="text-[11px] text-emerald-700" data-testid={`smart-import-protected-${p.person_key}-${f.field}`}>Dado cadastrado preservado.</p>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={reset} data-testid="smart-import-back">Trocar arquivos</Button>
              <Button onClick={confirm} disabled={saving || !totalChecked} data-testid="smart-import-confirm-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Aplicar {totalChecked} dado(s) ao cadastro
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
